import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { breakpoints } from '@avangard/ui/utils';
import { TextField } from '@avangard/ui/core';
import { MenuItem } from '@material-ui/core';

import { FormStyled } from '@modules/layout/styled';
import { ListToolbar } from '@modules/layout/moleculas';
import { SchoolLapsTableStats } from './school-laps-table-stats';
import { SchoolSummaryWidgetsStats } from './school-summary-widgets-stats';

import type { SchoolEntity } from '@modules/schools/entities';

type SchoolStatsFormGroupProps = {
    school: SchoolEntity;
};

const Form = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;

    > *:not(:last-child) {
        margin-bottom: 24px;
    }
`;

const StyledListToolbar = styled(ListToolbar)`
    flex-wrap: nowrap;
    margin-bottom: 24px;

    & > *:first-child {
        margin-bottom: 0;
    }

    & > *:last-child {
        max-width: 350px;
    }

    ${breakpoints.down('xs')} {
        flex-direction: column;

        & > *:first-child {
            margin-bottom: 24px;
        }

        & > *:last-child {
            max-width: 100%;
        }
    }
`;

const SchoolStatsFormGroup = (props: SchoolStatsFormGroupProps): React.ReactElement => {
    const { school } = props;

    const { t } = useTranslation(['common']);

    const [currentLapIdFilter, setCurrentLapIdFilter] = React.useState<string>('');

    const handleChangeLapFilter = React.useCallback(
        (event: React.ChangeEvent<{ value: string }>) =>
            setCurrentLapIdFilter(event.target.value ?? undefined),
        [],
    );

    const laps = school.lapAccessList
        .map(lapAccess => lapAccess?.lap ?? null)
        .filter(lap => lap?.isOnlineLap());

    return (
        <Form>
            <StyledListToolbar>
                <FormStyled.FormColumnTitle>
                    {t('common:forms.titles.approx_stats')}
                </FormStyled.FormColumnTitle>

                <TextField
                    select
                    fullWidth
                    id='lapId'
                    name='lapId'
                    placeholder={t('common:forms.placeholder.choose_lap') ?? ''}
                    value={currentLapIdFilter}
                    onChange={handleChangeLapFilter}
                >
                    <MenuItem value=''>{t('common:stubs.all_laps')}</MenuItem>

                    {laps.map(lap => (
                        <MenuItem key={lap?.id} value={lap?.id}>
                            {lap?.getFormattedName({ withPlatform: false })}
                        </MenuItem>
                    ))}
                </TextField>
            </StyledListToolbar>

            <SchoolSummaryWidgetsStats schoolId={school.id} lapId={currentLapIdFilter} />
            <SchoolLapsTableStats laps={laps} schoolId={school.id} />
        </Form>
    );
};

export { SchoolStatsFormGroup };
